/**
 * People IQ — HR Approval Queue.
 * Lists pending change requests submitted via self-service.
 * HR (approve_employee_change_request cap) can review + approve / reject each.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasCapability, formatDateTime } from "@/lib/permissions";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CheckCircle2, XCircle, ClipboardList, ChevronRight } from "lucide-react";

const STATUS_META = {
  pending: { label: "Pending", tint: "bg-amber-100 text-amber-800" },
  approved: { label: "Approved", tint: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Rejected", tint: "bg-rose-100 text-rose-700" },
  withdrawn: { label: "Withdrawn", tint: "bg-slate-100 text-slate-600" },
};

export default function ApprovalQueue() {
  const { user } = useAuth();
  const canDecide = hasCapability(user, "approve_employee_change_request");
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [decision, setDecision] = useState(null); // {cr, action}
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const params = statusFilter === "all" ? {} : { status: statusFilter };
      const { data } = await api.get("/people/change-requests", { params });
      setRows(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const decide = async () => {
    if (!decision) return;
    setBusy(true);
    try {
      await api.post(`/people/change-requests/${decision.cr.id}/${decision.action}`, { decision_notes: notes || null });
      toast.success(decision.action === "approve" ? "Request approved — record updated" : "Request rejected");
      setDecision(null); setNotes("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="approval-queue-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-semibold tracking-widest uppercase text-[#7C1D3A]">People IQ</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Approval Queue</h1>
          <p className="text-slate-500 mt-1 text-sm">Employee self-service change requests awaiting review.</p>
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="queue-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="withdrawn">Withdrawn</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          {rows.length === 0 ? (
            <div className="text-center py-16 text-slate-400 text-sm">
              <ClipboardList className="w-10 h-10 mx-auto mb-2 opacity-50" />
              No {statusFilter === "all" ? "" : statusFilter} requests.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3">Employee</th>
                  <th className="text-left px-4 py-3">Requested changes</th>
                  <th className="text-left px-4 py-3">Reason</th>
                  <th className="text-left px-4 py-3">Submitted</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody data-testid="queue-tbody">
                {rows.map((r) => {
                  const st = STATUS_META[r.status] || { label: r.status, tint: "bg-slate-100" };
                  return (
                    <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <Link to={`/people/${r.employee_id}`} className="font-medium text-[#7C1D3A] hover:underline flex items-center gap-1">
                          {r.employee_code} <ChevronRight className="w-3 h-3" />
                        </Link>
                        <div className="text-xs text-slate-500">{r.created_by_name}</div>
                        {r.channel === "candidate_form" && (
                          <Badge className="mt-0.5 bg-violet-100 text-violet-700 text-[9px] px-1 py-0">CANDIDATE FORM</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        <ul className="space-y-0.5">
                          {Object.entries(r.requested_changes || {}).map(([k, v]) => (
                            <li key={k}><span className="font-mono text-slate-600">{k}</span> → <b className="text-slate-900">{formatVal(v)}</b></li>
                          ))}
                        </ul>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600">{r.reason || "—"}</td>
                      <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{formatDateTime(r.created_at)}</td>
                      <td className="px-4 py-3"><Badge className={st.tint}>{st.label}</Badge></td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {r.status === "pending" && canDecide && (
                          <>
                            <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 mr-1" onClick={() => { setDecision({ cr: r, action: "approve" }); setNotes(""); }} data-testid={`approve-${r.id}`}>
                              <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                            </Button>
                            <Button size="sm" variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50" onClick={() => { setDecision({ cr: r, action: "reject" }); setNotes(""); }} data-testid={`reject-${r.id}`}>
                              <XCircle className="w-4 h-4 mr-1" /> Reject
                            </Button>
                          </>
                        )}
                        {r.status !== "pending" && r.decision_notes && (
                          <span className="text-xs text-slate-500 italic">{r.decision_notes}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!decision} onOpenChange={() => setDecision(null)}>
        <DialogContent data-testid="decide-dialog">
          <DialogHeader>
            <DialogTitle>
              {decision?.action === "approve" ? "Approve change request" : "Reject change request"}
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm space-y-3">
            <div className="text-xs text-slate-600">
              {decision?.action === "approve"
                ? "The employee record will be updated immediately with the values below and an audit entry will be written."
                : "The employee will see this request marked as rejected and can resubmit."}
            </div>
            {decision?.cr && (
              <div className="rounded border border-slate-200 bg-slate-50 p-3 text-xs">
                <div className="font-medium mb-1">{decision.cr.employee_code} · {decision.cr.created_by_name}</div>
                <ul className="space-y-0.5">
                  {Object.entries(decision.cr.requested_changes || {}).map(([k, v]) => (
                    <li key={k}><span className="font-mono">{k}</span> → <b>{formatVal(v)}</b></li>
                  ))}
                </ul>
              </div>
            )}
            <label className="text-xs font-semibold text-slate-600">Decision notes</label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={decision?.action === "reject" ? "Reason for rejection (recommended)" : "Optional"} data-testid="decision-notes" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecision(null)}>Cancel</Button>
            <Button
              onClick={decide}
              disabled={busy}
              className={decision?.action === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}
              data-testid="decision-confirm"
            >
              Confirm {decision?.action}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function formatVal(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "yes" : "no";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}
